export const COMPARISON_PERMISSION = "ui.reports.rollshop-mes-comparison";

export interface ComparisonConcept {
	id: string;
	name: string;
	dated: boolean;
	notes: string;
	dependentConcepts: string[];
}

export interface FieldDifference {
	field: string;
	rollshop: string | null;
	mes: string | null;
	different: boolean;
	delta: number | null;
	mesMoreComplete: boolean;
	updateUnavailableReason: string | null;
	equivalentName: boolean;
	informational: boolean;
}

export interface BarrelAssignment {
	rollshopName: string;
	rollshopId: string | null;
	mesName: string;
	mesId: string | null;
	rollsetCount: number;
}

export interface BarrelMatch {
	rollshopName: string;
	mesName: string;
	status: "Consistent" | "Possible" | "Ambiguous";
	reason: string;
	accepted: boolean;
	rollsetCount: number;
	rollshopTargets: number;
	mesSources: number;
	rollsets: { rollshop: string; mes: string; resolution: string }[];
	assignments?: BarrelAssignment[] | null;
}

export interface BarrelMatchSummary {
	commonRollsets: number;
	resolvedRollsets: number;
	missingMesBarrel: number;
	unresolvedRollshopBarrel: number;
	invalidMesBarrel: number;
	ignoredRollsets: number;
	consistent: number;
	possible: number;
	ambiguous: number;
}

export interface MatchOption {
	id: string;
	rollshopName: string | null;
	rollshopId: string | null;
	mesName: string | null;
	mesId: string | null;
	confirmed: boolean;
	reference: CatalogReference | null;
}

export interface DifferenceItem {
	key: string;
	type: string;
	timestamp: string | null;
	rollshopCount: number;
	mesCount: number;
	fields: FieldDifference[];
	diagnostic: string | null;
	mesMoreComplete: boolean;
	informationalOnly: boolean;
	updateFields: number;
	updateUnavailableReason: string | null;
	barrelMatch: BarrelMatch | null;
	barrelCandidates?: BarrelMatch[] | null;
	matchOption?: MatchOption | null;
	matchOptions?: DifferenceItem[] | null;
	reference?: CatalogReference | null;
	canInsert: boolean;
	insertUnavailableReason: string | null;
	equivalentBarrel: boolean;
}

export interface CaptureInfo {
	started: string;
	finished: string;
	rowsRead: number;
}

export interface RollCutDetail {
	id: string;
	date: string | null;
	diameter: number | null;
}

export interface RollCutHistory {
	rollshop: RollCutDetail[];
	mes: RollCutDetail[];
}

export interface RollAssignmentDetail {
	id: string;
	rollset: string | null;
	position: string | null;
	assignedDate: string | null;
	removedDate: string | null;
	removeStatus: string | null;
	removeReason: string | null;
	currentSlot: boolean;
}

export interface RollAssignmentHistory {
	rollshop: RollAssignmentDetail[];
	mes: RollAssignmentDetail[];
}

export interface CatalogOption {
	id: string;
	name: string;
}

export interface CatalogReference {
	concept: string;
	catalogName: string;
	name: string;
	missingSourceCatalog: boolean;
	targetId: string | null;
	targetName: string | null;
	canCreate: boolean;
	createUnavailableReason: string | null;
	usages: {
		location: string;
		count: number;
		catalogName: string;
		fieldName: string;
		recordSamples: string[];
	}[];
}

export interface ReferenceLinkChange {
	name: string;
	targetId: string;
	expectedTargetId: string | null;
}

export interface ComparisonResult {
	concept: string;
	runId: string;
	status: string;
	scope: string;
	timeZone: string;
	from: string;
	to: string;
	rollshopRows: number;
	mesRows: number;
	matched: number;
	onlyRollshop: number;
	onlyMes: number;
	changed: number;
	ambiguous: number;
	mappingIssues: number;
	notes: number;
	referencedNames: number;
	unresolvedReferences: number;
	undated: number;
	outsideRange: number;
	differenceItems: number;
	differentFields: number;
	mesMoreComplete: number;
	mesMoreCompleteFields: number;
	actionableFields: number;
	referenceIssueFields: number;
	pendingReferences: number;
	ambiguousValueDifferences: number;
	insertableRows: number;
	updatableRowsByCategory: Record<string, number>;
	barrelNameDifferences: number;
	equivalentNameFields: number;
	barrelMatching: BarrelMatchSummary | null;
	snapshotToken: string | null;
	rollshopCapture: CaptureInfo;
	mesCapture: CaptureInfo;
	examples: Record<string, DifferenceItem[]>;
}

export interface ConceptRun {
	concept: ComparisonConcept;
	status: "Queued" | "Running" | "Completed" | "Failed" | "Cancelled";
	result?: ComparisonResult;
	error?: string;
	stale?: boolean;
}

export function conceptStatus(row: ConceptRun): {
	label: string;
	color: "default" | "success" | "info" | "warning" | "error";
	description: string;
} {
	if (row.stale && row.status !== "Running") {
		return {
			label: "Refresh required",
			color: "warning",
			description: "Open or refresh this category to refresh its results.",
		};
	}
	if (!row.result || row.status !== "Completed") {
		return {
			label: row.status,
			color:
				row.status === "Failed"
					? "error"
					: row.status === "Cancelled"
						? "warning"
						: "default",
			description: row.error ?? row.status,
		};
	}
	const result = row.result;
	if (result.rollshopRows + result.mesRows === 0 && !result.referencedNames) {
		return {
			label: "No data",
			color: "default",
			description: "No records in this scope.",
		};
	}
	if (
		result.onlyRollshop > 0 ||
		result.actionableFields > (result.referenceIssueFields ?? 0) ||
		(result.ambiguousValueDifferences ?? 0) > 0
	) {
		return {
			label: "Differences",
			color: "error",
			description:
				"MES has missing records or different values beyond richer MES data.",
		};
	}
	const onlyMesRecords =
		result.onlyMes > 0 &&
		result.onlyMes + result.notes + result.referencedNames ===
			result.differenceItems;
	if (
		result.unresolvedReferences > 0 ||
		(result.pendingReferences ?? 0) > 0 ||
		(result.referenceIssueFields ?? 0) > 0 ||
		(result.mappingIssues > 0 && !onlyMesRecords)
	) {
		return {
			label: "References",
			color: "info",
			description:
				"Reference links or imported identities need review. No other value differences or missing MES records.",
		};
	}
	if (
		result.notes > 0 ||
		result.ambiguous > 0 ||
		(result.barrelMatching?.ambiguous ?? 0) > 0 ||
		(result.barrelMatching?.possible ?? 0) > 0
	) {
		return {
			label: "Notes",
			color: "info",
			description:
				"Duplicate records, missing business keys and uncertain matches are informational. No other value differences or missing MES records.",
		};
	}
	if (result.differenceItems === 0) {
		return {
			label: "Matched",
			color: "success",
			description: "The compared records match.",
		};
	}
	return {
		label: "Aligned",
		color: "info",
		description:
			"Remaining differences are optional details, cut dates, MES-only records, richer MES data or equivalent names.",
	};
}

export interface ReconciliationSelection {
	result: ComparisonResult;
	type: string;
	key: string | null;
	operation: "update" | "insert";
	matchOption?: MatchOption;
}

export interface ReconciliationPreview {
	operation: "update" | "insert";
	confirmationToken: string;
	concept: string;
	type: string;
	key: string | null;
	scope: string;
	from: string;
	to: string;
	timeZone: string;
	rowCount: number;
	referenceRowCount: number;
	skippedRows: number;
	affectedFields: Record<string, number>;
	page: number;
	pageSize: number;
	matchingRows: number;
	examples: {
		key: string;
		createsReference: boolean;
		matchOption?: MatchOption | null;
		changes: { field: string; mes: string | null; rollshop: string | null }[];
		preserved: string[];
	}[];
	exclusions: { key: string; reason: string }[];
	expires: string;
}

export interface ReconciliationOutcome {
	updatedRows: number;
	insertedRows: number;
	createdReferences: number;
	skippedRows: number;
	failedRows: number;
	requiresRerun: boolean;
}

export const DIFFERENCE_TYPES = [
	{ key: "all", label: "All differences", count: "differenceItems" },
	{ key: "Only Rollshop", label: "Only Rollshop", count: "onlyRollshop" },
	{ key: "Only MES", label: "Only MES (OK)", count: "onlyMes" },
	{ key: "Different values", label: "Different values", count: "changed" },
	{
		key: "MES more complete",
		label: "MES more complete (OK)",
		count: "mesMoreComplete",
	},
	{ key: "Ambiguous", label: "Duplicate / ambiguous", count: "ambiguous" },
	{ key: "Mapping issues", label: "Mapping issues", count: "mappingIssues" },
	{ key: "Notes", label: "Notes", count: "notes" },
	{
		key: "Referenced names",
		label: "Referenced names",
		count: "referencedNames",
	},
	{
		key: "Barrel names",
		label: "Barrels with different names",
		count: "barrelNameDifferences",
	},
] as const;
